import React from 'react';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../common/types';
import { Panel } from '../../components/primitives/Panel';
import type { BrowseUsageData } from '../hooks/useBrowseUsage';
import { browseCreditMessages as messages, browseMessages } from '../browseMessages';
import { BrowseCreditsMeter } from './BrowseCreditsMeter';
import { useResetCountdown } from './useResetCountdown';
/* eslint-disable css-modules/no-unused-class -- a shared stylesheet; each part uses its own slice of it */
import styles from './BrowseRail.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface BrowseCreditsSummaryProps {
  usage: BrowseUsageData;
}

/**
 * The body of the credits panel and the credits sheet (design a1 rail, c3): the credits left, the
 * meter, the reset, the rules and what the user opened today.
 */
const BrowseCreditsSummaryComponent: React.FC<BrowseCreditsSummaryProps> = ({ usage }) => {
  const intl = useIntl();
  const countdown = useResetCountdown(usage.resetsAt);

  return (
    <>
      <div className={styles.big}>
        <span className={styles.bigValue} data-testid="browse-credits-left">
          {usage.left}
        </span>
        <span className={styles.bigUnit}>{intl.formatMessage(messages.leftShort, { limit: usage.limit })}</span>
      </div>
      <BrowseCreditsMeter left={usage.left} limit={usage.limit} />
      <div className={styles.ticks}>
        <span>{intl.formatMessage(messages.used, { count: usage.used })}</span>
        {countdown !== null && <span>{intl.formatMessage(messages.resetsIn, { time: countdown })}</span>}
      </div>
      <p className={styles.rule}>{intl.formatMessage(messages.freeRules)}</p>
      <p className={styles.rule}>{intl.formatMessage(messages.costRule)}</p>
      {usage.openedToday.length > 0 && (
        <>
          <span className={styles.eyebrow}>
            {intl.formatMessage(messages.openedToday, { count: usage.openedToday.length })}
          </span>
          <Panel nested className={styles.openedList}>
            {usage.openedToday.map((item) => (
              <div key={`${item.kind}:${item.id}`} className={styles.openedItem}>
                <div>
                  <b>{item.name}</b>
                  <small>
                    {intl.formatMessage(item.kind === 'artist' ? browseMessages.kindArtist : browseMessages.kindTrack)}
                    {item.artistName !== null ? ` · ${item.artistName}` : ''}
                    {item.openedAt !== null
                      ? ` · ${intl.formatTime(item.openedAt, { hour: '2-digit', minute: '2-digit' })}`
                      : ''}
                  </small>
                </div>
              </div>
            ))}
          </Panel>
        </>
      )}
    </>
  );
};

export const BrowseCreditsSummary = TypedMemo(BrowseCreditsSummaryComponent);
